import { useId, useState } from "react";
import {
  ALL_ACCOUNTS,
  isCancellable,
  isWorkingOrder,
  OrderState,
  OrderType,
  Side,
  useConnection,
  useSettings,
  type OrderEvent,
} from "@helm/core";
import { formatCents } from "@helm/ui";
import { useTradingView, type PositionRow } from "../viewmodel/account";
import { useBusy, useGate } from "../trading/control";
import { cancelOrder, exitAction } from "../trading/actions";
import { SlideToConfirm } from "./SlideToConfirm";
import { Tabs, tabPanelProps } from "../components/Tabs";

const VIEWS = [
  ["positions", "Positions"],
  ["orders", "Orders"],
] as const;
type View = (typeof VIEWS)[number][0];
const enumLabel = (labels: Readonly<Partial<Record<number, string>>>, value: number): string =>
  labels[value]?.replaceAll("_", " ") ?? "Unknown";

/**
 * Mobile Positions (SPEC §17.7): the same account selection, trading view, gates and command
 * paths as the desktop tables. Every account scope is explicit and every exit requires a slide.
 */
export function Positions(): React.JSX.Element {
  const id = useId();
  const [view, setView] = useState<View>("positions");
  const trading = useTradingView();
  const connected = useConnection((s) => s.status === "connected");
  const privacy = useSettings((s) => s.settings.privacy);
  const all = trading.selection === ALL_ACCOUNTS;
  const accounts = trading.accounts.filter((a) => all || a.summary.id === trading.activeId);
  const bookCurrent = accounts.length > 0 && accounts.every((a) => a.openPositions !== null);
  const ids = new Set(accounts.map((a) => a.summary.id));
  const positions = trading.positions.filter((p) => ids.has(p.accountId));
  const orders = trading.orders.filter((o) => ids.has(o.accountId) && isWorkingOrder(o));
  const label = (account: string): string =>
    accounts.find((a) => a.summary.id === account)?.summary.label ?? account;
  return (
    <div className="m-positions">
      <Tabs
        tabs={VIEWS}
        value={view}
        onChange={setView}
        label="Positions or orders"
        idBase={id}
        variant="segmented"
      />
      {!connected && (
        <p role="status" className="muted m-note">
          Last known positions and orders — gateway disconnected.
        </p>
      )}
      <section {...tabPanelProps(id, view)} className="m-actions">
        {view === "positions" ? (
          positions.length ? (
            positions.map((p) => (
              <PositionCard key={p.key} position={p} account={label(p.accountId)} privacy={privacy} />
            ))
          ) : (
            <div className="empty-state m-card">
              <div className="empty-title">{bookCurrent ? "No Open Positions" : "Positions unconfirmed"}</div>
              <div className="muted">
                {bookCurrent
                  ? "Your active trades will appear here once an order is filled."
                  : "Waiting for confirmed broker position coverage; an empty list does not mean flat."}
              </div>
            </div>
          )
        ) : orders.length ? (
          orders.map((o) => <OrderCard key={o.clientOrderId} order={o} account={label(o.accountId)} />)
        ) : (
          <div className="empty-state m-card">
            <div className="empty-title">{bookCurrent ? "No Working Orders" : "Orders unconfirmed"}</div>
            <div className="muted">
              {bookCurrent
                ? "Orders you place will appear here."
                : "Waiting for current broker account coverage."}
            </div>
          </div>
        )}
      </section>
      {accounts.map((a) => (
        <AccountActions key={a.summary.id} id={a.summary.id} label={a.summary.label} />
      ))}
      <p className="muted m-note">
        Exit &amp; Cxl cancels that symbol&apos;s orders and closes its position at market. Flatten cancels
        all orders and closes every position on the named account.
      </p>
    </div>
  );
}

function PositionCard({
  position: p,
  account,
  privacy,
}: {
  position: PositionRow;
  account: string;
  privacy: boolean;
}): React.JSX.Element {
  const key = `mobile:exit:${p.accountId}:${p.symbol}`;
  const gate = useGate({ kind: "exitSymbol", accountId: p.accountId, contract: p.symbol });
  const busy = useBusy(key);
  return (
    <article className="m-card m-actions" data-mobile-position={p.key}>
      <strong>
        {p.symbol} · {p.netQty > 0 ? "Long" : "Short"} {Math.abs(p.netQty)}
      </strong>
      <span>{account}</span>
      <span className="num">
        Position estimate {formatCents(p.unrealizedCents, { privacy, signed: true })}
      </span>
      <SlideToConfirm
        label={`Exit & Cxl ${p.symbol} · ${account}`}
        tone={p.netQty > 0 ? "sell" : "buy"}
        disabled={!gate.ok || busy}
        onConfirm={() => {
          void exitAction(
            key,
            "EXIT_AND_CANCEL_SYMBOL",
            p.accountId,
            p.symbol,
            `Exit & Cxl ${p.symbol} · ${account}`,
          );
        }}
      />
      {!gate.ok && <span className="muted m-note">{gate.reason}</span>}
      {busy && <span role="status">Exit request pending…</span>}
    </article>
  );
}

function OrderCard({ order: o, account }: { order: OrderEvent; account: string }): React.JSX.Element {
  const key = `mobile:cancel:${o.clientOrderId}`;
  const gate = useGate({ kind: "cancel", accountId: o.accountId });
  const busy = useBusy(key);
  return (
    <article className="m-card m-actions" data-mobile-order={o.clientOrderId}>
      <strong>
        {o.symbol} · {o.side === Side.BUY ? "Buy" : "Sell"} {o.qty - o.filled}
      </strong>
      <span>
        {account} · {enumLabel(OrderType, o.type)} · {enumLabel(OrderState, o.state)}
      </span>
      <span className="muted m-note">
        Filled {o.filled} of {o.qty}
      </span>
      <SlideToConfirm
        label={`Cancel ${o.symbol} order · ${account}`}
        tone="neutral"
        disabled={!gate.ok || busy || !isCancellable(o)}
        onConfirm={() => {
          void cancelOrder(key, o.accountId, o.clientOrderId, `Cancel ${o.symbol} order · ${account}`);
        }}
      />
      {!gate.ok && <span className="muted m-note">{gate.reason}</span>}
      {busy && <span role="status">Cancel request pending…</span>}
    </article>
  );
}

function AccountActions({ id, label }: { id: string; label: string }): React.JSX.Element {
  const cancelKey = `mobile:cancel-all:${id}`;
  const flattenKey = `mobile:flatten:${id}`;
  const cancelGate = useGate({ kind: "cancelAll", accountId: id });
  const flattenGate = useGate({ kind: "flattenAccount", accountId: id });
  const cancelling = useBusy(cancelKey);
  const flattening = useBusy(flattenKey);
  return (
    <section className="m-card m-actions" aria-label={`Account actions ${label}`}>
      <strong>{label}</strong>
      <SlideToConfirm
        label={`Cancel All · ${label}`}
        tone="neutral"
        disabled={!cancelGate.ok || cancelling}
        onConfirm={() => {
          void exitAction(cancelKey, "CANCEL_ALL_ACCOUNT", id, null, `Cancel All · ${label}`);
        }}
      />
      {!cancelGate.ok && <span className="muted m-note">{cancelGate.reason}</span>}
      <SlideToConfirm
        label={`Flatten · ${label}`}
        tone="flatten"
        disabled={!flattenGate.ok || flattening}
        onConfirm={() => {
          void exitAction(flattenKey, "FLATTEN_ACCOUNT", id, null, `Flatten · ${label}`);
        }}
      />
      {!flattenGate.ok && <span className="muted m-note">{flattenGate.reason}</span>}
      {(cancelling || flattening) && <span role="status">Request pending…</span>}
    </section>
  );
}
